<template>
  <div class="language-selector">
    <button
      :aria-expanded="isDropdownOpen"
      @click="toggleDropdown"
      class="language-select-btn"
      :aria-label="t('header-menu.select-language')"
      :id="'language-select'"
      >
      {{ getSelectedLanguageLabel() }}
    </button>
    
    <ul
      v-if="isDropdownOpen"
      :aria-labelledby="'language-select'"
      class="language-options"
      @keydown="handleKeydown"
    >
      <li
        v-for="language in languages"
        :key="language.code"
      >
        <button
        :aria-current="currentLanguage === language.code"
        tabindex="0"
        @click="selectLanguage(language.code)"
        @keydown.enter.prevent="selectLanguage(language.code)"
        @keydown.space.prevent="selectLanguage(language.code)"
        :class="{'active': currentLanguage === language.code}"
        :lang="language.code"
        >
          {{ isMobile ? language.code : language.label }}
        </button>
      </li>
    </ul>
  </div>
</template>

<script>
import { ref, onMounted, onBeforeUnmount } from 'vue';
import { useI18n } from 'vue-i18n';
import languageService from '../services/languageService';

export default {
  name: 'LanguageSelector',
  setup() {
    const { t, locale } = useI18n();

    // Define supported languages
    const languages = [
      { code: 'es', label: 'Castellano' },
      { code: 'en', label: 'English' }
    ];

    // Determine default language
    const getDefaultLanguage = () => {
      const primaryLanguage = (navigator.languages && navigator.languages[0]) || navigator.language;
      const languageCode = primaryLanguage.split('-')[0]; // Get base language code
      return ['es', 'en'].includes(languageCode) ? languageCode : 'es'; // Default to Spanish if unsupported
    };

    const savedLanguage = localStorage.getItem('language');
    const currentLanguage = ref(savedLanguage || getDefaultLanguage());
    locale.value = currentLanguage.value; // Set initial locale

    const isDropdownOpen = ref(false);
    const isMobile = ref(false);

    const handleResize = () => {
      isMobile.value = window.innerWidth <= 768;
    };

    onMounted(() => {
      handleResize();
      window.addEventListener('resize', handleResize);
    });

    onBeforeUnmount(() => {
      window.removeEventListener('resize', handleResize);
    });

    const toggleDropdown = () => {
      isDropdownOpen.value = !isDropdownOpen.value;
    };

    const closeDropdown = () => {
      isDropdownOpen.value = false;
    };

    const selectLanguage = (languageCode) => {
      languageService.setLanguage(languageCode);
      locale.value = languageCode; 
      currentLanguage.value = languageCode;
      closeDropdown();
    };

    const getSelectedLanguageLabel = () => {
      const selected = languages.find(lang => lang.code === currentLanguage.value);
      if (selected) {
        return isMobile.value ? selected.code : selected.label;
      }
      return '';
    };

    const handleKeydown = (event) => {
      const listItems = document.querySelectorAll('.language-options li');
      const activeIndex = Array.from(listItems).findIndex(item => item.classList.contains('active'));

      if (event.key === 'ArrowDown') {
        const nextIndex = (activeIndex + 1) % listItems.length;
        listItems[nextIndex].focus();
      } else if (event.key === 'ArrowUp') {
        const prevIndex = (activeIndex - 1 + listItems.length) % listItems.length;
        listItems[prevIndex].focus();
      } else if (event.key === 'Escape') {
        closeDropdown();
      }
    };

    return {
      t,
      languages,
      currentLanguage,
      isDropdownOpen,
      toggleDropdown,
      selectLanguage,
      getSelectedLanguageLabel,
      handleKeydown,
      isMobile
    };
  }
};
</script>
